import { decodeBoatClass } from '@/network/decoders'
import EventBoat from '@model/EventBoat'
import EventCategory from '@model/EventCategory'
import EventClass from '@model/EventClass'
import EventCourse from '@model/EventCourse'
import EventCrew from '@model/EventCrew'
import EventLifeguard from '@model/EventLifeguard'
import EventRace from '@model/EventRace'
import EventRaceSkippedClass from '@model/EventRaceSkippedClass'
import EventReferee from '@model/EventReferee'
import EventResult from '@model/EventResult'
import EventsRepo from '@repo/EventsRepo'
import { useRepo } from 'pinia-orm'

export function createEventFromFile ( content ) {
	const storedEvent = useRepo( EventsRepo ).createEvent( {
		title: content.event.title,
		ctlId: parseInt( content.event.ctl ),
		from: content.event.from.toDate(),
		to: content.event.to === null ? null : content.event.to.toDate(),
		venueId: content.event.venueId,
		clubId: content.event.clubId,
		organizer: content.event.organizer,
		otherVenue: content.event.otherVenue,
		registrationUrl: content.event.registrationUrl,
		noticeboardUrl: content.event.noticeboardUrl,
		description: content.event.description,
		sponsor: content.event.sponsor,
		director: content.event.director,
		plannedRaces: content.event.plannedRaces,
		created: content.event.created.toDate(),
		noLifeguards: content.event.noLifeguards,
		rrsUuid: content.event.rrsUuid,
		ignoreUpdateMembers: content.event.ignoreUpdateMembers,
		forceEnglishReports: content.event.forceEnglishReports ?? false,
		descInResultsTitle: content.event.descInResultsTitle
	} )

	useRepo( EventsRepo ).save( storedEvent )

	content.event.courses?.forEach( function ( eventCourse ) {
		useRepo( EventCourse ).save( {
			eventId: storedEvent.id,
			title: eventCourse.title,
		} )
	} )

	content.event.classes.forEach( function ( eventClass ) {
		const storedClass = useRepo( EventClass ).save( {
			eventId: storedEvent.id,
			boatClassId: decodeBoatClass( eventClass.boatClassId ),
			weight: eventClass.weight,
			eventType: eventClass.eventType,
			shortcut: eventClass.shortcut,
			discardDefinition: eventClass.discardDefinition,
			className: ( eventClass.className !== null && eventClass.className !== undefined ) ? eventClass.className : null,
			publicLicenseExceptionType: ( eventClass.publicLicenseExceptionType !== null && eventClass.publicLicenseExceptionType !== undefined ) ? eventClass.publicLicenseExceptionType : 0,
			days: ( eventClass.days !== null && eventClass.days !== undefined ) ? eventClass.days : null,
			startDate: ( eventClass.startDate !== null && eventClass.startDate !== undefined ) ? eventClass.startDate.toDate() : null
		} )

		eventClass.categories?.forEach( function ( category ) {
			useRepo( EventCategory ).save( {
				eventClassId: storedClass.id,
				shortcut: category.shortcut,
				title: category.title,
				sex: category.sex,
				ageUnder: category.ageUnder,
				ageAbove: category.ageAbove,
				otherLimit: category.otherLimit,
			} )
		} )
	} )

	content.event.referees.forEach( function ( referee ) {
		useRepo( EventReferee ).save( {
			eventId: storedEvent.id,
			refereeId: referee.refereeId,
			refereeRoleId: referee.refereeRoleId,
		} )
	} )

	content.event.lifeguards.forEach( function ( lifeguard ) {
		useRepo( EventLifeguard ).save( {
			eventId: storedEvent.id,
			lifeguardId: lifeguard.lifeguardId,
			lifeguardRoleId: lifeguard.lifeguardRoleId,
		} )
	} )

	const boats = new Map<string, string>

	content.event.boats.forEach( function ( boat ) {
		const storedBoat = useRepo( EventBoat ).save( {
			eventId: storedEvent.id,
			boatClassId: decodeBoatClass( boat.boatClassId ),
			country: boat.country,
			sailNumber: boat.sailNumber,
			name: boat.name,
			detail: boat.detail,
			paid: boat?.paid ?? 0,
		} )

		boats.set( boat.id, storedBoat.id )

		boat.crew.forEach( function ( crew ) {
			useRepo( EventCrew ).save( {
				eventBoatId: storedBoat.id,
				memberId: crew.memberId,
				captain: crew.captain,
				order: crew.order,
				nonMemberRegId: crew.nonMemberRegId,
				nonMemberFirstName: crew.nonMemberFirstName,
				nonMemberLastName: crew.nonMemberLastName,
				nonMemberSex: crew.nonMemberSex,
				nonMemberBirthDate: crew.nonMemberBirthDate === null ? null : crew.nonMemberBirthDate.toDate(),
				nonMemberBirthCode: crew.nonMemberBirthCode,
				nonMemberCitizenship: crew.nonMemberCitizenship,
				nonMemberAddress: crew.nonMemberAddress,
				nonMemberEmail: crew.nonMemberEmail,
				nonMemberPhone: crew.nonMemberPhone,
			} )
		} )
	} )

	content.event.races.forEach( function ( race ) {
		const eventRace = useRepo( EventRace ).save( {
			eventId: storedEvent.id,
			race: race.race,
		} )

		race.skippedClasses.forEach( function ( skipClass ) {
			useRepo( EventRaceSkippedClass ).save( {
				eventRaceId: eventRace.id,
				boatClassId: decodeBoatClass( skipClass.boatClassId ),
			} )
		} )

		race.results.forEach( function ( result ) {
			useRepo( EventResult ).save( {
				eventBoatId: boats.get( result.eventBoatId ),
				eventRaceId: eventRace.id,
				order: result.order,
				other: result.other,
				attr: result.attr,
				counter: result.counter ?? 0
			} )
		} )
	} )

	return storedEvent.id
}
